import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Loader2, Clock, Zap, ExternalLink, ChevronRight, Lock, AlertTriangle, Copy, Mail, CircleDollarSign } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Admin · Vinci Price Lock Board.
 *
 * Kanban view over `GET /api/admin/price-lock/board` with a lane per
 * stage (Booking Sent → Booking Paid → Settlement Sent → Fully Paid →
 * Expired). Booking Sent cards render a live 4h countdown ticking every
 * second. Booking Paid cards expose a one-click "Issue Settlement Now"
 * override that skips the scheduled 4h wait.
 *
 * Endpoints
 * ---------
 * GET  /api/admin/price-lock/board
 * POST /api/admin/buyer-quotations/{qid}/issue-settlement-now
 */

const LANE_COLOUR = {
  booking_sent: "#D8B23E",
  booking_paid: "#2FB999",
  settlement_sent: "#B87333",
  fully_paid: "#8AD8B0",
  expired: "#C0392B",
};

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

function pad2(n) { return String(n).padStart(2, "0"); }
function formatHMS(ms) {
  if (ms <= 0) return "00:00:00";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

function CountdownBadge({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const left = new Date(deadline).getTime() - now;
  const expired = left <= 0;
  const urgent = !expired && left < 30 * 60 * 1000;
  const colour = expired ? "#C0392B" : urgent ? "#C0392B" : "#D8B23E";
  return (
    <span data-testid="plb-countdown"
          className="inline-flex items-center gap-1.5 font-mono text-xs px-2 py-1 rounded-sm border"
          style={{ color: colour, borderColor: colour + "66", background: colour + "12" }}>
      <Clock size={11} />
      {expired ? "Expired" : formatHMS(left)}
      {urgent && !expired && (
        <span className="ml-1 font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#C0392B]">
          ⚡ urgent
        </span>
      )}
    </span>
  );
}

function Card({ card, lane, api, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const runIssueSettlement = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/buyer-quotations/${card.id}/issue-settlement-now`);
      if (!data.payment_link_url) {
        toast.warning(
          `Settlement ${data.settlement_invoice_number} minted (${inr(data.settlement_amount)}), ` +
          `but Razorpay rejected the pay link — invoice is in DRAFT, ` +
          `no buyer link generated. Amount may exceed test-mode cap; ` +
          `fix in Razorpay dashboard and retry.`,
          { duration: 12000 });
      } else {
        toast.success(
          `Settlement ${data.settlement_invoice_number} · ${inr(data.settlement_amount)} issued`);
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue settlement");
    } finally { setBusy(false); setConfirmOpen(false); }
  };
  const bookingLine = card.booking_invoice_number
    ? `${card.booking_invoice_number} · ${inr(card.booking_amount)}`
    : "—";
  const settlementLine = card.settlement_invoice_number
    ? `${card.settlement_invoice_number} · ${inr(card.settlement_amount)}`
    : lane === "booking_paid" ? "Awaiting override / 4h auto-issue" : "—";

  return (
    <motion.div layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        data-testid={`plb-card-${card.id}`}
        className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4 hover:border-white/20 transition-colors space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="font-serif text-sm text-[#F5F5F0] leading-tight">
          {card.quotation_number}
        </div>
        {lane === "booking_sent" && (
          <CountdownBadge deadline={card.booking_deadline} />
        )}
      </div>
      <div className="text-[11px] text-[#8A8A82] font-body truncate">
        {card.buyer_name || card.buyer_email} · <span className="text-[#D8B23E]">{inr(card.total)}</span>
      </div>
      <div className="pt-2 border-t border-white/[0.06] space-y-1">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] font-grotesk">
          <span>Booking · 30%</span>
          <span className="font-mono text-[10px] normal-case tracking-normal text-[#C9C9C2]">
            {bookingLine}
          </span>
        </div>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] font-grotesk">
          <span>Settlement · 70%</span>
          <span className="font-mono text-[10px] normal-case tracking-normal text-[#C9C9C2]">
            {settlementLine}
          </span>
        </div>
      </div>
      {card.t30_reminder_sent && lane === "booking_sent" && (
        <div className="text-[9px] text-[#C0392B] font-grotesk uppercase tracking-[0.18em]">
          ⏳ T-30 reminder sent
        </div>
      )}
      {lane === "booking_paid" && (
        <>
          <button type="button" onClick={() => setConfirmOpen(true)} disabled={busy}
                  data-testid={`plb-issue-settlement-${card.id}`}
                  className="w-full mt-1 bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
            Issue Settlement Now
          </button>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent data-testid="plb-issue-settlement-confirm"
                                className="bg-[#0a0a0a] border border-[#B87333]/40 text-[#F5F5F0]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
                  <AlertTriangle size={18} className="text-[#B87333]" />
                  Issue Final Settlement now?
                </AlertDialogTitle>
                <AlertDialogDescription className="font-body text-sm text-[#C9C9C2] leading-relaxed pt-2">
                  This will mint the <b className="text-[#D8B23E]">70% Final Settlement invoice</b>{" "}
                  for <b>{card.quotation_number}</b> and email the buyer the balance
                  payment link <b>immediately</b>, skipping the scheduled 4-hour wait.
                  <br /><br />
                  Only proceed if the buyer has confirmed early settlement.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="plb-issue-settlement-cancel"
                                    className="bg-transparent border border-white/20 text-[#C9C9C2] hover:bg-white/[0.06]">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction data-testid="plb-issue-settlement-confirm-yes"
                                    onClick={runIssueSettlement} disabled={busy}
                                    className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk uppercase tracking-[0.18em] text-[10px]">
                  {busy ? "Issuing…" : "Yes, issue now"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
      {lane === "settlement_sent" && card.settlement_payment_link_url && (
        <a href={card.settlement_payment_link_url} target="_blank" rel="noreferrer"
           className="text-[10px] text-[#B87333] hover:text-[#D8B23E] font-grotesk uppercase tracking-[0.18em] inline-flex items-center gap-1">
          Settlement pay link <ExternalLink size={9} />
        </a>
      )}
      {lane === "expired" && (
        <div className="text-[10px] text-[#C0392B] font-grotesk uppercase tracking-[0.18em]">
          {card.expiry_reason === "booking_window_lapsed"
            ? "Booking window lapsed"
            : (card.expiry_reason || "expired")}
        </div>
      )}
      {card.booking_balance_amount > 0 && (
        <BookingBalancePanel card={card} api={api} onChanged={onChanged} />
      )}
    </motion.div>
  );
}

function BookingBalancePanel({ card, api, onChanged }) {
  const [sending, setSending] = useState(false);
  const payUrl = card.booking_balance_payment_link_url || "";
  const sentAt = card.booking_balance_email_sent_at;

  const copyLink = async () => {
    if (!payUrl) {
      toast.warning("No Razorpay pay-link on this balance invoice — check ledger");
      return;
    }
    try {
      await navigator.clipboard.writeText(payUrl);
      toast.success("Balance pay-link copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — copy manually from the ledger");
    }
  };

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/buyer-quotations/${card.id}/resend-booking-balance-email`);
      toast.success(
        `Balance email dispatched to ${data.sent_to} · ${data.invoice_number}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail
                  || "Could not resend balance email");
    } finally { setSending(false); }
  };

  return (
    <div data-testid={`plb-balance-panel-${card.id}`}
         className="mt-2 border border-[#D8B23E]/40 rounded-sm bg-[#1B1401]/60 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span data-testid={`plb-balance-pill-${card.id}`}
              className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E] bg-[#D8B23E]/15 border border-[#D8B23E]/50 px-2 py-1 rounded-sm">
          <CircleDollarSign size={10} />
          Balance Pending · {inr(card.booking_balance_amount)} due
        </span>
        <span className="font-mono text-[10px] text-[#8A8A82]">
          {card.booking_balance_invoice_number || "—"}
        </span>
      </div>
      {card.booking_balance_already_paid > 0 && (
        <div className="text-[10px] text-[#8A8A82] font-body leading-relaxed">
          Received so far: <b className="text-[#2FB999]">{inr(card.booking_balance_already_paid)}</b>
          <span className="mx-1.5 text-[#4a4a45]">·</span>
          Target 30%: <b className="text-[#C9C9C2]">{inr(card.booking_balance_amount + card.booking_balance_already_paid)}</b>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={copyLink} disabled={!payUrl}
                data-testid={`plb-balance-copy-${card.id}`}
                className="flex-1 border border-white/20 hover:border-[#D8B23E] hover:text-[#D8B23E] text-[#C9C9C2] font-grotesk text-[9px] uppercase tracking-[0.22em] px-2 py-1.5 rounded-sm inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
          <Copy size={10} /> Copy Pay-Link
        </button>
        <button type="button" onClick={resend} disabled={sending}
                data-testid={`plb-balance-resend-${card.id}`}
                className="flex-1 bg-[#D8B23E] hover:bg-[#F6E4A6] text-black font-grotesk text-[9px] uppercase tracking-[0.22em] px-2 py-1.5 rounded-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
          {sending ? "Sending…" : sentAt ? "Resend" : "Send Email"}
        </button>
      </div>
      {sentAt && (
        <div className="text-[9px] text-[#6f6f68] font-body">
          Last sent · {new Date(sentAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

/**
 * One-time button — mints the two Feb-2026 Booking Balance invoices
 * (VNC/QT/2026/05104 + VNC/QT/2026/05108). Idempotent on the server
 * so a double-click just returns the existing balance invoices.
 */
function OneTimeBalanceMintButton({ api, onDone }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);

  const runMint = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/one-time/generate-booking-balance-invoices");
      setResults(data.results || []);
      const minted = (data.results || []).filter(r => r.balance_invoice_number).length;
      toast.success(`${minted} balance invoice(s) minted · scroll down for pay-links`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail
                  || "Mint failed — check backend logs");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" data-testid="plb-one-time-mint" onClick={() => setOpen(true)}
              className="bg-[#D8B23E] hover:bg-[#F6E4A6] text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm inline-flex items-center gap-1.5">
        <CircleDollarSign size={12} /> Mint Balance Invoices
      </button>
      <button type="button" data-testid="plb-backfill-cascade"
              onClick={async () => {
                try {
                  const { data } = await api.post(
                    "/admin/one-time/backfill-booking-balance-cascade");
                  const advanced = (data.results || []).filter(r => r.status === "advanced").length;
                  const already = (data.results || []).filter(r => r.status === "already-advanced").length;
                  toast.success(`Sync · ${advanced} advanced to booking_paid, ${already} already there`);
                  onDone && onDone();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Sync failed — check backend logs");
                }
              }}
              className="bg-[#2FB999] hover:bg-[#3FD9B0] text-black font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm inline-flex items-center gap-1.5">
        <Zap size={12} /> Sync Paid Cascade
      </button>
      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setResults(null); } }}>
        <AlertDialogContent data-testid="plb-one-time-mint-modal"
                              className="bg-[#0a0a0a] border border-[#D8B23E]/40 text-[#F5F5F0] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
              <CircleDollarSign size={18} className="text-[#D8B23E]" />
              Mint the two Booking Balance invoices
            </AlertDialogTitle>
            <AlertDialogDescription className="font-body text-sm text-[#C9C9C2] leading-relaxed pt-2 space-y-3">
              <div>
                One-time mint for the Feb-2026 partial-payment cases.
                <b className="text-[#F5F5F0]"> Idempotent</b> — if the
                invoices already exist, the server returns them without
                double-issuing.
              </div>
              <ul className="list-none space-y-2 text-[13px]">
                <li className="border border-white/10 rounded-sm p-2.5">
                  <span className="font-mono text-[11px] text-[#D8B23E]">VNC/QT/2026/05108</span>{" "}
                  <span className="text-[#8A8A82]">→ balance</span>{" "}
                  <b className="text-[#F5F5F0]">₹49,485</b>{" "}
                  <span className="text-[#8A8A82]">(already paid ₹1,77,000)</span>
                </li>
                <li className="border border-white/10 rounded-sm p-2.5">
                  <span className="font-mono text-[11px] text-[#D8B23E]">VNC/QT/2026/05104</span>{" "}
                  <span className="text-[#8A8A82]">→ balance</span>{" "}
                  <b className="text-[#F5F5F0]">₹23,121</b>{" "}
                  <span className="text-[#8A8A82]">(already paid ₹2,36,000)</span>
                </li>
              </ul>
              <div className="text-[12px] text-[#8A8A82]">
                Emails are <b className="text-[#F5F5F0]">not</b> sent
                automatically — you'll press <b>Send Email</b> on each
                Kanban card after this mints.
              </div>
              {results && (
                <div className="mt-3 border-t border-white/10 pt-3 space-y-2">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#2FB999]">
                    Mint result
                  </div>
                  {results.map((r, i) => (
                    <div key={i} data-testid={`plb-mint-result-${i}`}
                          className="text-[11px] font-mono text-[#C9C9C2] border border-white/10 rounded-sm p-2">
                      <div><b>{r.quotation_number}</b> · {r.status || "ok"}</div>
                      {r.balance_invoice_number && (
                        <div>Invoice: <span className="text-[#D8B23E]">{r.balance_invoice_number}</span>
                          {" "}· ₹{Number(r.balance_amount).toLocaleString("en-IN")}</div>
                      )}
                      {r.razorpay_payment_link_url && (
                        <div className="truncate">
                          <a href={r.razorpay_payment_link_url} target="_blank" rel="noreferrer"
                             className="text-[#B87333] hover:text-[#D8B23E]">
                            {r.razorpay_payment_link_url}
                          </a>
                        </div>
                      )}
                      {r.reason && <div className="text-[#C0392B]">{r.reason}</div>}
                      {r.warning && <div className="text-[#C0392B]">{r.warning}</div>}
                    </div>
                  ))}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plb-one-time-cancel" disabled={busy}
                                className="bg-transparent border border-white/20 text-[#C9C9C2] hover:bg-white/[0.06]">
              {results ? "Close" : "Cancel"}
            </AlertDialogCancel>
            {!results && (
              <AlertDialogAction data-testid="plb-one-time-confirm" onClick={(e) => { e.preventDefault(); runMint(); }}
                                   disabled={busy}
                                   className="bg-[#D8B23E] hover:bg-[#F6E4A6] text-black font-grotesk uppercase tracking-[0.18em] text-[10px]">
                {busy ? (<><Loader2 size={11} className="animate-spin mr-1" /> Minting…</>) : "Yes, mint both invoices"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Lane({ lane, label, cards, stats, api, onChanged }) {
  const colour = LANE_COLOUR[lane] || "#8A8A82";
  return (
    <div data-testid={`plb-lane-${lane}`}
         className="w-72 shrink-0 border border-white/10 rounded-sm bg-black/40 flex flex-col">
      <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between"
           style={{ background: colour + "10" }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: colour }} />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em]"
               style={{ color: colour }}>{label}</div>
        </div>
        <div className="text-[10px] font-mono text-[#8A8A82]">
          {stats.count} · {inr(stats.total)}
        </div>
      </div>
      <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto min-h-[120px]">
        <AnimatePresence>
          {cards.length === 0 ? (
            <div className="text-center text-[11px] text-[#6f6f68] font-body italic py-6">
              Empty lane
            </div>
          ) : cards.map((c) => (
            <Card key={c.id} card={c} lane={lane} api={api} onChanged={onChanged} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

export function AdminPriceLockBoard({ api }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/price-lock/board");
      setBoard(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Price-Lock board");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    load();
    // Auto-refresh the board every 30 s so scheduled sweeps + webhook flips
    // land on-screen without the admin having to hit Refresh.
    timer.current = setInterval(load, 30_000);
    return () => clearInterval(timer.current);
  }, [load]);

  const summary = useMemo(() => {
    if (!board) return null;
    let count = 0, total = 0;
    for (const l of board.lanes || []) { count += l.stats.count; total += l.stats.total; }
    return { count, total };
  }, [board]);

  return (
    <div data-testid="admin-price-lock-board" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#B87333] inline-flex items-center gap-1.5">
            <Lock size={11} /> Vinci Price Lock Engine
          </div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2">Price Lock Board</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl leading-relaxed">
            Every 30% Booking → 70% Settlement in flight, bucketed by lane
            with a live 4-hour countdown on each active booking. Ops can
            override the scheduled settlement to issue the balance invoice
            immediately when the buyer confirms early.
          </p>
        </div>
        <button data-testid="plb-refresh" onClick={load}
                className="border border-white/20 hover:border-[#B87333] font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm text-[#C9C9C2] hover:text-[#B87333] inline-flex items-center gap-1.5">
          <RefreshCw size={12} /> Refresh
        </button>
        <OneTimeBalanceMintButton api={api} onDone={load} />
      </div>

      {summary && (
        <div className="rounded-sm border border-[#D8B23E]/30 bg-[#1B1401]/60 px-5 py-3 flex items-center justify-between">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">
            Locked exposure across every lane
          </div>
          <div className="font-serif text-2xl text-[#F5F5F0]">
            {inr(summary.total)}{" "}
            <span className="text-sm text-[#8A8A82] font-body ml-1">· {summary.count} deals</span>
          </div>
        </div>
      )}

      {loading && !board ? (
        <div className="p-12 text-center text-[#8A8A82] font-body text-sm">
          <Loader2 size={18} className="animate-spin inline mr-2" /> Loading board…
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-3">
          {(board?.lanes || []).map((l) => (
            <Lane key={l.key} lane={l.key} label={l.label} cards={l.cards}
                  stats={l.stats} api={api} onChanged={load} />
          ))}
        </div>
      )}

      <div className="text-[10px] text-[#6f6f68] font-body">
        <ChevronRight size={10} className="inline" /> Auto-refreshes every 30 seconds ·
        countdown ticks in real-time · scheduler sweeps expiry & settlement every 5 min ·
        T-30 reminder every minute.
      </div>
    </div>
  );
}
